import { useEffect, useState } from "react";
import Tooltip from "./ui/Tooltip";
import ModalBackdrop from "./ui/ModalBackdrop";
import { PROVENANCE_LEGEND } from "../lib/provenance";

interface Props {
  open: boolean;
  onClose: () => void;
  // The currently-active case's forwarding address, shown prominently
  // inside section 1 so the user can copy it straight from the guide.
  // Null when no case is open — the guide surfaces a short note in
  // that state and prompts the user to create one.
  activeCaseForwardAddress: string | null;
}

// Getting-started guide — opens as a full-page overlay on top of the
// app shell, matching the Settings overlay's card + header + body
// structure so the visual treatment stays consistent.
export default function Guide({
  open,
  onClose,
  activeCaseForwardAddress,
}: Props) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCopied(false);
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  async function copyAddress() {
    if (!activeCaseForwardAddress) return;
    try {
      await navigator.clipboard.writeText(activeCaseForwardAddress);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* user can select manually */
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="guide-title"
        className="settings-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="settings-header">
          <h2 id="guide-title" className="modal-title">
            Getting the most from CaseThread
          </h2>
          <Tooltip title="Close guide">
            <button
              type="button"
              className="btn-icon"
              onClick={onClose}
              aria-label="Close guide"
            >
              ✕
            </button>
          </Tooltip>
        </div>

        <div className="settings-body">
          <section className="settings-section">
            <h3>1. Forward your emails automatically</h3>
            <p>
              You can CC or forward emails to your case address manually,
              but for the best experience, set up an automatic forwarding
              rule — then emails file themselves without you thinking
              about it.
            </p>
            <p>
              <strong>In Gmail:</strong> Settings → Filters and Blocked
              Addresses → Create a new filter → From: [the other party's
              email] → Forward to your case's forwarding address (shown at
              the top of each case). Every matching email is filed
              automatically.
            </p>
            <p>
              <strong>In Outlook:</strong> Settings → Mail → Rules → Add
              new rule → From [the other party] → Forward to your case
              address.
            </p>
            <p>
              <strong>To backfill:</strong> open the original email thread
              and forward the entire conversation to your case address.
              CaseThread will split it into individual records.
            </p>
            {activeCaseForwardAddress ? (
              <div className="guide-forward-row">
                <span className="guide-forward-label">
                  This case's forwarding address:
                </span>
                <code className="guide-forward-address">
                  {activeCaseForwardAddress}
                </code>
                <button
                  type="button"
                  className="guide-forward-copy"
                  onClick={copyAddress}
                >
                  {copied ? "Copied ✓" : "Copy"}
                </button>
              </div>
            ) : (
              <p className="muted">
                Each case gets its own unique forwarding address — create a
                case to see yours.
              </p>
            )}
          </section>

          <section className="settings-section">
            <h3>2. Upload files by dragging</h3>
            <p>
              Screenshots, photos, PDFs, letters, bank statements — drag
              them straight from your desktop, Downloads folder, or file
              manager into the Upload panel. No need to organise files
              into folders first. iPhone photos (HEIC) are converted
              automatically.
            </p>
          </section>

          <section className="settings-section">
            <h3>3. Paste screenshots instantly</h3>
            <p>
              See something on screen you want to capture? Take a
              screenshot (Cmd+Shift+4 on Mac, Win+Shift+S on Windows),
              then go to your case, click + Add → Paste screenshot, and
              paste (Cmd+V / Ctrl+V). CaseThread extracts the text
              automatically.
            </p>
          </section>

          <section className="settings-section">
            <h3>4. Record phone calls</h3>
            <p>
              Before a call, click + Add → Record call. CaseThread
              captures the audio from your microphone and transcribes it
              automatically when you stop recording. The transcript
              becomes a searchable record.
            </p>
          </section>

          <section className="settings-section">
            <h3>5. Let the AI work for you</h3>
            <p>Once you have a few records filed:</p>
            <ul className="guide-list">
              <li>
                <strong>Summary</strong> generates a concise overview of
                your entire case
              </li>
              <li>
                <strong>Suggested Action</strong> tells you the single best
                next step
              </li>
              <li>
                <strong>Interrogate</strong> lets you ask questions about
                your case — "What did they promise?", "What are my
                deadlines?", "Draft a response to their last email"
              </li>
              <li>
                <strong>Key Facts</strong> extracts every date, amount,
                reference number, and commitment
              </li>
              <li>
                <strong>Story</strong> writes a flowing narrative of
                everything that happened
              </li>
              <li>
                <strong>Timeline</strong> generates a one-page visual
                chronology of your case — export it as a PDF to attach
                to complaints or share with a lawyer
              </li>
            </ul>
          </section>

          <section className="settings-section">
            <h3>What the marks mean</h3>
            <p>
              <span className="confidence-dot confidence-green prov-mark" />{" "}
              verified · <span className="prov-ai-chip">AI</span> AI
              assistance. {PROVENANCE_LEGEND}
            </p>
          </section>

          <section className="settings-section">
            <h3>6. Keep your case current</h3>
            <p>
              The more records you file, the smarter the AI gets. Forward
              every email, upload every letter, record every call. A
              complete record is your strongest tool.
            </p>
          </section>
        </div>
      </div>
    </ModalBackdrop>
  );
}
